import { useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import { Link, useOutletContext, useNavigate } from 'react-router-dom';

import { Formik, Form } from 'formik';
import {
  FormikInput as Input,
  FormikPasswordInput as PasswordInput,
  useTranslator,
  useToast,
  setAuthCredentials,
  useAppContext,
} from '@front-activerse/common-components';
import validationSchema from './validationSchema';

const AuthLoginByEmailAndPasswordForm = ({
  login,
  redirectToPathAfterSuccess,
  userDataOnDemand,
  comesFromStripe = false,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const { trackFirebaseEvent } = useOutletContext();
  const navigate = useNavigate();
  const [error, setError] = useState(false);

  const {
    images: { eye, eyeSlash },
  } = useAppContext();

  const initialValues = {
    email: '',
    password: '',
  };

  const handleSubmit = async (values, { resetForm }) => {
    const request = {
      email: values.email,
      password: values.password,
    };

    setError(false);

    try {
      const response = await login(request);
      const { __typename, token } = response.data.auth.login;

      if (__typename === 'AuthLoginSuccess') {
        setAuthCredentials(token);
        await userDataOnDemand();
        resetForm();

        if (comesFromStripe) {
          trackFirebaseEvent('user_action', {
            action: 'tap-stripe_login',
            location: 'stripe_login ',
            used_account: 'mail',
          });
        }
        navigate(redirectToPathAfterSuccess, { replace: true });
      } else {
        setError(true);
      }
    } catch (e) {
      toastError();
      throw new Error(`Failed to login, got error: ${e}`);
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      validateOnChange
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ isValid, isSubmitting }) => {
        return (
          <Form>
            <Input
              name="email"
              type="email"
              label={t('inputLabel.emailAddress')}
              autoComplete="username"
              translateErrMsg
            />
            <PasswordInput
              name="password"
              type="password"
              label={t('inputLabel.password')}
              autoComplete="current-password"
              iconEye={eye}
              iconEyeSlash={eyeSlash}
              translateErrMsg
            />
            {error && (
              <p className="error my-xl text-center">
                {t('logIn.validationError.invalidEmailOrPassword')}
              </p>
            )}

            <div className="text-center mt-xl">
              <Button
                type="submit"
                color="primary"
                className="w-100  mb-m"
                disabled={!isValid || isSubmitting}
                data-test-id="log_in_button"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />

                    {t('logIn.loggingIn.button')}
                  </>
                ) : (
                  t('logIn.logIn.button')
                )}
              </Button>

              <Link to={'/auth/remind-password'} className="mt-s d-inline-block">
                {t('logIn.forgotPassword.button')}
              </Link>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AuthLoginByEmailAndPasswordForm;
